import React from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useReactionTypes, ReactionType } from '../hooks/usePostReaction';
import { Button } from '@/components/ui/button';

interface ReactionFilterProps {
  selectedReaction: string | null;
  onSelectReaction: (reactionId: string | null) => void;
  className?: string;
}

export function ReactionFilter({ 
  selectedReaction, 
  onSelectReaction,
  className 
}: ReactionFilterProps) {
  const { data: reactionTypes, isLoading } = useReactionTypes();
  
  if (isLoading || !reactionTypes) {
    return null;
  }
  
  return (
    <div className={cn("relative", className)}>
      <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-hide">
        {/* All Vibes button */}
        <button
          onClick={() => onSelectReaction(null)}
          className={cn(
            "shrink-0 px-3 py-1.5 rounded-full text-sm font-medium transition-all",
            selectedReaction === null
              ? "bg-primary text-primary-foreground"
              : "bg-muted text-muted-foreground hover:bg-muted/80"
          )}
        >
          All Vibes
        </button>
        
        {/* Reaction type buttons */}
        {reactionTypes.map((reaction) => (
          <button
            key={reaction.id}
            onClick={() => onSelectReaction(
              selectedReaction === reaction.id ? null : reaction.id
            )}
            className={cn(
              "shrink-0 flex items-center gap-1 px-2.5 py-1.5 rounded-full text-sm transition-all",
              selectedReaction === reaction.id
                ? "bg-primary/10 text-primary ring-2 ring-primary/30"
                : "bg-muted text-muted-foreground hover:bg-muted/80"
            )}
            title={reaction.label}
          >
            <span className="text-base">{reaction.emoji}</span>
            <span className="hidden sm:inline">{reaction.label}</span>
          </button>
        ))}
      </div>
      
      {/* Active filter indicator */}
      {selectedReaction && (
        <div className="flex items-center justify-between mt-2 px-1">
          <span className="text-xs text-muted-foreground">
            Showing {reactionTypes.find(r => r.id === selectedReaction)?.emoji}{' '}
            {reactionTypes.find(r => r.id === selectedReaction)?.label} posts
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={() => onSelectReaction(null)}
          >
            <X className="h-3 w-3 mr-1" />
            Clear
          </Button>
        </div>
      )}
    </div>
  );
}
